import React from "react";
import {
  AssetType,
  DigitalMetadata,
  AssetFormData,
} from "../../../types/asset-types";
import {
  BaseAssetFormProps,
  AssetFormHeader,
  FormField,
  TextInput,
  SelectInput,
  NumberInput,
  AIConversationFields,
} from "./asset-form-base";

const PLATFORM_TYPE_OPTIONS = [
  { value: "website", label: "Website" },
  { value: "social", label: "Social Media" },
  { value: "email", label: "Email" },
  { value: "app", label: "Mobile App" },
  { value: "other", label: "Other" },
];

export function DigitalAssetForm({
  data,
  onChange,
  errors,
  disabled,
}: BaseAssetFormProps) {
  const metadata = (data.metadata as Partial<DigitalMetadata>) || {};

  const handleMetadataChange = (field: keyof DigitalMetadata, value: any) => {
    const newMetadata = {
      ...metadata,
      [field]: value,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  const handleAIContentChange = (aiData: any) => {
    const newMetadata = {
      ...metadata,
      ...aiData,
    };
    onChange({
      ...data,
      metadata: newMetadata,
    });
  };

  return (
    <div className="space-y-6">
      <AssetFormHeader
        type={AssetType.DIGITAL}
        description="Create a digital asset like a website banner, social media post, email signature, or app advertisement."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField
          label="Platform"
          required
          error={errors?.["metadata.platform"]}
          description="Name of the digital platform or service"
        >
          <TextInput
            value={metadata.platform || ""}
            onChange={(value) => handleMetadataChange("platform", value)}
            placeholder="Facebook, Google Ads, company.com, Mobile App"
            disabled={disabled}
            required
            data-testid="digital-platform-input"
          />
        </FormField>

        <FormField
          label="Platform Type"
          required
          error={errors?.["metadata.platformType"]}
          description="Type of digital platform"
        >
          <SelectInput
            value={metadata.platformType || ""}
            onChange={(value) => handleMetadataChange("platformType", value)}
            options={PLATFORM_TYPE_OPTIONS}
            placeholder="Select platform type"
            disabled={disabled}
            required
            data-testid="digital-platform-type-select"
          />
        </FormField>
      </div>

      <FormField
        label="Conversation ID"
        error={errors?.["metadata.conversationId"]}
        description="Platform-specific conversation identifier (optional)"
      >
        <TextInput
          value={metadata.conversationId || ""}
          onChange={(value) => handleMetadataChange("conversationId", value)}
          placeholder="FB-2024-001, GOOG-SPRING-PROMO"
          disabled={disabled}
        />
      </FormField>

      <FormField
        label="Placement"
        required
        error={errors?.["metadata.placement"]}
        description="Where the asset appears on the platform"
      >
        <TextInput
          value={metadata.placement || ""}
          onChange={(value) => handleMetadataChange("placement", value)}
          placeholder="Header banner, Sidebar ad, News feed, Email footer"
          disabled={disabled}
          required
          data-testid="digital-placement-input"
        />
      </FormField>

      <FormField
        label="Target Audience"
        error={errors?.["metadata.targetAudience"]}
        description="Description of the intended audience"
      >
        <TextInput
          value={metadata.targetAudience || ""}
          onChange={(value) => handleMetadataChange("targetAudience", value)}
          placeholder="Adults 25-45, Tech professionals, Local residents"
          disabled={disabled}
          data-testid="digital-target-audience-input"
        />
      </FormField>

      <FormField
        label="Impressions"
        error={errors?.["metadata.impressions"]}
        description="Expected or actual number of impressions"
      >
        <NumberInput
          value={metadata.impressions}
          onChange={(value) => handleMetadataChange("impressions", value)}
          placeholder="10000"
          disabled={disabled}
          min={0}
          data-testid="digital-impressions-input"
        />
      </FormField>

      <AIConversationFields
        data={metadata}
        onChange={handleAIContentChange}
        errors={errors}
        disabled={disabled}
      />
    </div>
  );
}
